import type { IReward } from "@api/models/IReward";
import ButtonVariants from "@core-components/button/ButtonVariants";
import GradientBorder from "@core-components/gradient-border/GradientBorder";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Image, Stack, Text, ThemeIcon } from "@mantine/core";
import useUserStore from "@store/user.store";
import { IconGiftFilled } from "@tabler/icons-react";
import Coins from "./Coins";
import ContainerCard from "./container/ContainerCard";

type IRewardCardProps = {
  onBuy: (rewardId: number) => void;
  reward: IReward;
};

const RewardCard: React.FC<IRewardCardProps> = ({ onBuy, reward }) => {
  const { affiliationInformation } = useUserStore();
  const canUserBuy =
    (affiliationInformation?.coinBalance ?? 0) >= reward.rewardPrice;
  const canBeBought = canUserBuy && reward.isBuyable;

  const { t } = useAppTranslation("pages.clientDashboard.components");
  const rewardsT = useAppTranslation();

  const computeButtonLabel = () => {
    if (!reward.isBuyable) return t("rewardCard.unavailable");
    if (!canBeBought) return t("rewardCard.notEnoughCoins");
    return t("rewardCard.unlock");
  };

  return (
    <ContainerCard
      title={
        <>
          {rewardsT.t(`${reward.i18nKey}.label`)}
          {reward.productPrice && (
            <span className="text-primary-dark-text text-sm">
              &nbsp;(~{reward.productPrice}€)
            </span>
          )}
        </>
      }
      classNames={{
        root: "w-full xs:w-auto",
        title: "text-center",
        wrapper: "!pt-0 !px-0",
      }}
    >
      <Image src={reward.image} />
      <Stack className="!mt-0 px-2 pt-0">
        <GradientBorder
          className="gradient-gray-to-light-white rounded-[4px]"
          childrenClassName="p-4 !bg-[#202125] text-primary-light-text font-inter"
        >
          {rewardsT.t(`${reward.i18nKey}.description`)}
        </GradientBorder>

        <Text
          className={
            "flex items-center justify-center gap-1 font-inter text-primary-white"
          }
        >
          <Coins size={44} />
          <span className="font-semibold text-4xl">{reward.rewardPrice}</span>
        </Text>
        <Flex className="justify-center">
          <ButtonVariants.Gradient
            disabled={!canBeBought}
            className="gradient-blue-light-purple hover-scale mb-4 rounded-lg disabled:grayscale-[0.3]"
            onClick={() => onBuy(reward.id)}
            label={
              <>
                <ThemeIcon
                  className="mx-2"
                  color={canBeBought ? "white" : "gray"}
                  variant="transparent"
                  size={16}
                >
                  <IconGiftFilled />
                </ThemeIcon>
                {computeButtonLabel()}
              </>
            }
          />
        </Flex>
      </Stack>
    </ContainerCard>
  );
};

export default RewardCard;
